import { z } from 'zod';
import { artifactEffectsSchema } from './artifacts';
import {
  decimalStringSchema,
  digestSchema,
  huntSessionStateSchema,
  loadoutSchema,
  progressionSchema,
} from './hunt';
import { inventoryInstanceSchema } from './inventory';
import { activePetSchema } from './pets';

/**
 * Equipping an item the player owns (ADR 0005, prompt 3C).
 *
 * The ONE mutating loadout route. Note what the request cannot say: no slot,
 * no rarity, no level, no shipIndex, no bonus, no projectile count. It names
 * an instance and nothing else - the server infers the slot from the
 * definition's category and recomputes every number from the catalog. A
 * client that lies about anything is simply naming an item it does not own.
 */

export const LOADOUT_EQUIP_ROUTE = '/api/loadout/equip' as const;

export const loadoutEquipRequestSchema = z
  .object({
    /** The instance to equip. Must belong to the caller and be usable. */
    itemInstanceId: z.uuid(),
    /**
     * The loadout version the client believes it is changing.
     *
     * Optimistic concurrency in the open: two tabs that both try to equip
     * from the same view carry the same number, and only the first can win.
     * The loser is told to refresh rather than silently overwriting.
     */
    expectedLoadoutVersion: z.number().int().min(0),
    /** Replay guard for THIS request. Never used as a ledger key. */
    idempotencyKey: z.string().min(8).max(128),
  })
  .strict();
export type LoadoutEquipRequest = z.infer<typeof loadoutEquipRequestSchema>;

export const loadoutEquipResponseSchema = z.object({
  /** The complete loadout after the change - both slots, always. */
  loadout: loadoutSchema,
  /** Unchanged by an equip: artifacts have no slot and are never equipped. */
  artifactTotals: artifactEffectsSchema,
  /** Unchanged too - the pet has its own slot and its own routes. */
  activePet: activePetSchema.nullable(),
  /** The version to send with the next equip. */
  loadoutVersion: z.number().int().min(0),
  /** The item that moved into a slot, in its post-change inventory shape. */
  equipped: inventoryInstanceSchema,
  /** The item that left the slot, now free again. Null on the first equip. */
  unequipped: inventoryInstanceSchema.nullable(),
  /**
   * The SAME session, reconfigured in place when one was active. The arena is
   * never recreated: mobs, boss, timers and the RNG all continue.
   */
  session: huntSessionStateSchema,
  snapshot: z.unknown(),
  snapshotDigest: digestSchema,
  progression: progressionSchema,
  /** What the pending tail earned under the OLD loadout, before the swap. */
  settled: z.object({
    validatedMs: z.number().int().min(0),
    xpEarned: decimalStringSchema,
    goldEarned: decimalStringSchema,
    levelsGained: decimalStringSchema,
  }),
  /** True when the same key was already applied; nothing changed again. */
  replayed: z.boolean(),
});
export type LoadoutEquipResponse = z.infer<typeof loadoutEquipResponseSchema>;
